import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../navbar";
import Header from "../../header";
import { useSelector } from "react-redux";
import { getUserCertificates } from "../../../store/reducers/applicationSlice";
import { downloadCertificate } from "../../../store/reducers/fileSlice";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const formatDate = (inputDate) => {
  if (!inputDate) return "-";
  const date = new Date(inputDate);
  if (Number.isNaN(date.getTime())) return "-";
  const options = { year: "numeric", month: "long", day: "2-digit" };
  return date.toLocaleDateString(undefined, options);
};

const hasDatePassed = (dateString) => {
  if (!dateString) return false;
  const inputDate = new Date(dateString);
  if (Number.isNaN(inputDate.getTime())) return false;
  return inputDate < new Date();
};

const getExpiryDate = (item) => item?.due_date || item?.expired_at;

const Certifications = () => {
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    async function getCertificates() {
      setLoading(true);
      try {
        const response = await getUserCertificates({
          userId: user.user.id,
          authToken,
        });
        const list = Array.isArray(response?.applications)
          ? response.applications
          : response?.applications?.data ?? [];
        setCertificates(list);
      } catch (error) {
        console.error("Error fetching certificates:", error);
        setCertificates([]);
      } finally {
        setLoading(false);
      }
    }

    if (authToken && user?.user?.id) {
      getCertificates();
    }
  }, [authToken, user]);

  const filteredCertificates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return certificates.filter((item) => {
      const expired = hasDatePassed(getExpiryDate(item));
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "valid" && !expired) ||
        (statusFilter === "expired" && expired);

      if (!matchesStatus) return false;
      if (!query) return true;

      return Object.values(item).some(
        (val) => val && val.toString().toLowerCase().includes(query)
      );
    });
  }, [certificates, searchQuery, statusFilter]);

  const paginatedData = useMemo(() => {
    return filteredCertificates.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filteredCertificates, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCertificates.length / itemsPerPage) || 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) =>
          (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("name", {
        header: "Certificate Name",
        cell: (info) => {
          const name = info.getValue() || "-";
          return (
            <div className="boardTitle">
              <strong title={name}>
                {name.length > 50 ? `${name.substring(0, 50)}...` : name}
              </strong>
            </div>
          );
        },
      }),
      columnHelper.accessor("issue_date", {
        header: "Issued",
        cell: (info) => {
            return <span>{formatDate(info.getValue())}</span>;
        }, 
      }),
      columnHelper.display({
        id: "expire",
        header: "Expire",
        cell: (info) => {
          const expiry = getExpiryDate(info.row.original);
          const expired = hasDatePassed(expiry);
          return (
            <span className={expired ? "due" : ""}>{formatDate(expiry)}</span>
          );
        },
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell: (info) => {
          const expired = hasDatePassed(getExpiryDate(info.row.original));
          return (
            <span className={expired ? "due" : ""}>
              {expired ? "Expired" : "Valid"}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id={`certificateDropdown-${row.id}`}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby={`certificateDropdown-${row.id}`}
                >
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item"
                      onClick={(e) => {
                        e.preventDefault();
                        downloadCertificate(row.id, authToken);
                      }}
                    >
                      Download
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [authToken, currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Certifications</h2>
            </div>
            <div
              className="boardHeadRight"
              style={{
                gap: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-start",
              }}
            >
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                }}
              >
                <option value="all">All</option>
                <option value="valid">Valid</option>
                <option value="expired">Expired</option>
              </select>
              <form className="searchForm" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => setSearchQuery(e.target.value)}
                  value={searchQuery}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
            </div>
          </div>

          <div className="boardContent table-responsive">
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      <p>No Certificate Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default Certifications;
